<template>
    <div id="change-email-wrap">
      <form v-if="!pinSent" @submit.prevent="initiateEmailChange">
        <Input 
          type="email" 
          name="current_email" 
          id="current_user_email" 
          label="Current Email" 
          v-model="currentEmail" 
          :readonly="true" 
          autocomplete="email"
        />
        <Input 
          type="email" 
          name="new_email" 
          id="new_user_email" 
          label="New Email" 
          v-model="newEmail" 
          autocomplete="email"
        />
        <Input 
          type="email" 
          name="confirm_new_email" 
          id="confirm_new_email" 
          label="Confirm New Email" 
          v-model="confirmNewEmail" 
          autocomplete="email"
        />
        <Input 
          type="password" 
          label="Password" 
          v-model="password" 
          name="password" 
          id="passwordInput" 
          autocomplete="current-password"
        />
        <ModalMessage :message="feedbackMessage" :type="feedbackType" style="margin-top: 1rem;"/>
        <input type="submit" class="fa-btn fa-btn-primary fa-btn-modal" value="Send Verification PIN">
      </form>
      <form v-else @submit.prevent="confirmEmailChange">
        <Input 
          type="text" 
          name="pin" 
          id="pin" 
          label="Verification PIN" 
          v-model="pin" 
        />
        <input type="submit" class="fa-btn fa-btn-primary fa-btn-modal" value="Confirm Email Change" :disabled="isSubmitDisabled">
        <ModalMessage :message="feedbackMessage" :type="feedbackType" style="margin-top: 1rem;"/>
      </form>

    </div>
  </template>
  
  <script setup>
  import { ref, computed } from 'vue';
  import { useUserStore } from '~/store/userStore';
  import { MessageType } from '@/types/enums.ts';
  
  const isSubmitDisabled = ref(false);

  const userStore = useUserStore();
  
  const currentEmail = computed(() => userStore.userData?.email_address || '');
  const newEmail = ref('');
  const confirmNewEmail = ref('');
  const password = ref('');
  const pin = ref('');
  const pinSent = ref(false);
  const requestId = ref('');
  
  const feedbackMessage = ref('');
  const feedbackType = ref(MessageType.INFO);
  
  const initiateEmailChange = async () => {
      // Reset feedback
    feedbackMessage.value = '';
    feedbackType.value = MessageType.INFO;

    // Validation checks
    if (newEmail.value !== confirmNewEmail.value) {
        feedbackMessage.value = 'New email and confirmation email do not match.';
        feedbackType.value = MessageType.ERROR;
        return;
    }

    if (newEmail.value === currentEmail.value) {
        feedbackMessage.value = 'New email cannot be the same as the current email.';
        feedbackType.value = MessageType.ERROR;
        return;
    }
  
    const emailChangeData = {
      current_email: currentEmail.value,
      new_email: newEmail.value,
      password: password.value
    };
  
    try {
      const response = await fetch(`${import.meta.env.VITE_WP_BASE_URL}/wp-json/fa/v1/initiate-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': import.meta.env.VITE_WP_TOKEN
        },
        body: JSON.stringify(emailChangeData)
      });
  
      const data = await response.json();
  
      if (data.success) {
        feedbackMessage.value = data.message;
        feedbackType.value = MessageType.SUCCESS;
        pinSent.value = true;
        requestId.value = data.request_id;
      } else {
        feedbackMessage.value = data.message || 'Failed to initiate email change. Please try again.';
        feedbackType.value = MessageType.ERROR;
      }
    } catch (error) {
      console.error('Error initiating email change:', error);
      feedbackMessage.value = 'An error occurred. Please try again later.';
      feedbackType.value = MessageType.ERROR;
    }
  };
  
  const confirmEmailChange = async () => {
    const confirmData = {
      request_id: requestId.value,
      pin: pin.value
    };
  
    try {
      const response = await fetch(`${import.meta.env.VITE_WP_BASE_URL}/wp-json/fa/v1/confirm-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': import.meta.env.VITE_WP_TOKEN
        },
        body: JSON.stringify(confirmData)
      });
  
      const data = await response.json();
  
      if (data.success) {
        feedbackMessage.value = 'Email change successfully submitted. It will take a few days until the changes take effect.';
        feedbackType.value = MessageType.SUCCESS;
        isSubmitDisabled.value = true;
      } else {
        feedbackMessage.value = data.message || 'Failed to confirm email change. Please try again.';
        feedbackType.value = MessageType.ERROR;
      }
    } catch (error) {
      console.error('Error confirming email change:', error);
      feedbackMessage.value = 'An error occurred. Please try again later.';
      feedbackType.value = MessageType.ERROR;
    }
  };
  </script>